/**
 * La scheda di un progetto sul telefono (7/10/2026): risponde a "a che punto
 * è questo lavoro, e cosa c'è da fare?".
 *
 * Prima, a 390px, era la copia stretta del computer: il titolo su due righe,
 * sette linguette che scorrevano, quattro numeri in griglia (Team 0, 0
 * interni), poi blocchi con le etichette maiuscole e schede ASSEGNATO /
 * STATO / PRIORITÀ / SCADENZA per ogni task. Il lavoro promesso al cliente
 * stava in un'altra linguetta.
 * Qui una pagina sola, nell'ordine in cui servono le cose:
 *   1. di chi è e che cos'è (il cliente piccolo sopra, il lavoro grande);
 *   2. la risposta: i lavori previsti fatti (o l'avanzamento), chi tiene
 *      fermo qualcosa, la consegna;
 *   3. i lavori previsti, con lo stato che si cambia toccando la riga;
 *   4. i task da fare, che si spuntano col pollice;
 *   5. le ultime voci del Diario;
 *   6. i soldi in poche righe;
 *   7. il resto (timeline, documenti, team, note, cliente) un tocco più in là.
 * Le linguette del computer diventano sezioni che si aprono da qui
 * (?tab=task, timeline, documenti, diario, gestione): gli indirizzi di sempre
 * continuano a funzionare. Stessa chiamata e stessa cache del computer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, calcMargin } from '../../lib/utils';
import { Icon } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MEmpty, MGroup, MHead, MHero, MPage, MRow, MScelte } from '../../components/mobile/MobileKit';
import { DIVISION_LABEL } from '@masamune/shared';
import type { ProjectFull } from './ProjectDetailPage';
import { canWriteProjects } from './projectsApi';
import { LavoriMobile, riassuntoLavori } from './LavoriMobile';
import { cosaE, dataParlata, giorniA, nomeCliente, STATO_PROGETTO } from './ProgettiMobile';
import {
  ComunicazioneSheet, NuovoTaskSheet, RigaComunicazione, RigaTask, giornoBreve, ordinaTask, personaBreve,
  type Comunicazione,
} from './pjmPezzi';
import { SezioneDiario, SezioneDocumenti, SezioneGestione, SezioneTask, SezioneTimeline } from './ProgettoSezioniMobile';
import { ContoRigheMobile } from './ContoMobile';
import './progetti-mobile.css';

type Tab = 'overview' | 'lavori' | 'task' | 'timeline' | 'documenti' | 'diario' | 'gestione';
const TABS: Tab[] = ['overview', 'lavori', 'task', 'timeline', 'documenti', 'diario', 'gestione'];
/** Gli stessi alias dei vecchi nomi della scheda del computer. */
const TAB_ALIASES: Record<string, Tab> = {
  comunicazioni: 'diario', attivita: 'diario', team: 'gestione', economics: 'gestione', budget: 'gestione', margini: 'gestione', marginalita: 'gestione',
};

const euro = (n: number) => `€ ${fmtEur(n)}`;

export function ProgettoMobile() {
  const { id } = useParams<{ id: string }>();
  const [rawTab, setTab] = useUrlState<string>('tab', 'overview');
  const tab: Tab = (TABS as string[]).includes(rawTab) ? (rawTab as Tab) : (TAB_ALIASES[rawTab] ?? 'overview');
  const [nuovoTask, setNuovoTask] = useState(false);
  const openModal = useModalStore((s) => s.open);

  const { data: p, isLoading, isError } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get<ProjectFull>(`/projects/${id}`),
    enabled: !!id,
    retry: false,
  });

  // Aprendo una sezione si riparte dall'alto: senza, la sezione nuova si
  // apriva già scesa all'altezza a cui si era arrivati nella Panoramica.
  useEffect(() => {
    document.querySelector('.os-main')?.scrollTo({ top: 0 });
  }, [tab]);

  if (!id) return <Navigate to="/" replace />;
  if (isError) {
    return (
      <MPage>
        <MGroup title="Progetto non trovato">
          <MEmpty>L'indirizzo non corrisponde a nessun progetto, oppure il tuo profilo non può vederlo.</MEmpty>
        </MGroup>
      </MPage>
    );
  }
  if (isLoading || !p) return <MPage><MEmpty>Carico il progetto…</MEmpty></MPage>;

  // "Lavori previsti" sul telefono è il primo gruppo della Panoramica.
  const sezione = tab === 'lavori' ? 'overview' : tab;

  let barra: ReactNode = null;
  if (sezione === 'overview' || sezione === 'task') {
    barra = <button className="btn btn-primary" onClick={() => setNuovoTask(true)}><Icon name="plus" size={12} /> Nuovo task</button>;
  } else if (sezione === 'documenti') {
    barra = <button className="btn btn-primary" onClick={() => openModal('uploadDoc', { scope: 'project', scopeId: p.id })}><Icon name="upload" size={12} /> Carica un documento</button>;
  } else if (sezione === 'diario') {
    barra = <button className="btn btn-primary" onClick={() => openModal('newComm', { projectId: p.id })}><Icon name="pencil" size={12} /> Scrivi nel diario</button>;
  } else if (sezione === 'timeline') {
    barra = <button className="btn btn-primary" onClick={() => openModal('newMilestone', { projectId: p.id })}><Icon name="plus" size={12} /> Nuova milestone</button>;
  }

  return (
    <div className="pjm">
      {sezione === 'overview' && <Panoramica p={p} onSezione={(t) => setTab(t)} />}
      {sezione === 'task' && <SezioneTask p={p} />}
      {sezione === 'timeline' && <SezioneTimeline p={p} />}
      {sezione === 'documenti' && <SezioneDocumenti p={p} />}
      {sezione === 'diario' && <SezioneDiario p={p} iniziale={rawTab === 'attivita' ? 'attivita' : 'comunicazioni'} />}
      {sezione === 'gestione' && <SezioneGestione p={p} />}

      {barra && <div className="m-actionbar m-only">{barra}</div>}
      <NuovoTaskSheet projectId={p.id} open={nuovoTask} onClose={() => setNuovoTask(false)} />
    </div>
  );
}

// ── La Panoramica ─────────────────────────────────────────────────────────

type Foglio = 'nota' | 'fatture' | 'abbonamenti' | 'consegne' | null;

function Panoramica({ p, onSezione }: { p: ProjectFull; onSezione: (t: Tab) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const scrive = canWriteProjects(user);
  const [comm, setComm] = useState<Comunicazione | null>(null);
  const [foglio, setFoglio] = useState<Foglio>(null);
  const [stato, setStato] = useState(false);

  // La stessa chiamata della bacheca del computer, senza la posizione: il
  // progetto cambia colonna e resta nel suo ordine.
  const sposta = useMutation({
    mutationFn: (status: string) => api.post(`/projects/${p.id}/move`, { status }),
    onSuccess: (_r, status) => {
      qc.invalidateQueries({ queryKey: ['project', p.id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.ok('Stato del progetto aggiornato', STATO_PROGETTO[status] ?? status);
    },
    onError: (e: any) => toast.alert('Stato non aggiornato', e?.message ?? 'Riprova'),
  });

  const r = riassuntoLavori(p.tasks);
  const chiuso = p.status === 'CHIUSO';
  const ritardo = p.deadline && !chiuso ? -giorniA(p.deadline) : 0;
  const consegna: ReactNode = p.deadline
    ? ritardo > 0
      ? <span className="is-alert">consegna superata da {ritardo === 1 ? '1 giorno' : `${ritardo} giorni`}</span>
      : `${chiuso ? 'consegnato entro' : 'consegna'} il ${dataParlata(p.deadline)}`
    : 'senza data di consegna';

  // La risposta: con i lavori previsti caricati conta quelli (sono il conto
  // dell'avanzamento anche per il server), senza conta la fase.
  let label: string;
  let value: ReactNode;
  const sotto: ReactNode[] = [];
  if (p.alerts.length) sotto.push(<span className="is-alert">{p.alerts[0]!.message}{p.alerts.length > 1 ? ` (+${p.alerts.length - 1})` : ''}</span>);
  if (r.totale > 0) {
    label = 'Lavori previsti fatti';
    value = <>{r.fatti}<span className="pjm-di">su {r.totale}</span></>;
    if (r.attesaCliente) sotto.push(<span className="is-warn">{r.attesaCliente === 1 ? '1 fermo' : `${r.attesaCliente} fermi`}: aspettiamo il cliente</span>);
    if (r.fermiNoi) sotto.push(<span className="is-alert">{r.fermiNoi === 1 ? '1 fermo da noi' : `${r.fermiNoi} fermi da noi`}</span>);
  } else if (p.progress > 0) {
    label = 'Avanzamento';
    value = <>{p.progress}<span className="pjm-di">%</span></>;
    if (p.phase) sotto.push(p.phase);
  } else {
    label = 'A che punto è';
    value = <span className="pjm-frase">{p.phase || STATO_PROGETTO[p.status] || p.status}</span>;
  }
  sotto.push(consegna);

  // Da fare: i task che NON sono lavori previsti (quelli stanno nel gruppo sopra).
  const aperti = p.tasks.filter((t) => !t.inScope && t.status !== 'DONE').sort(ordinaTask);
  const ultime = [...p.communications].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)).slice(0, 3);

  // Soldi, dalle fatture che arrivano con il progetto (come il filo dei soldi del computer).
  const fattureValide = (p.invoices ?? []).filter((i) => i.status !== 'ANNULLATA');
  const fatturato = fattureValide.reduce((a, i) => a + (Number(i.total) || 0), 0);
  const incassato = fattureValide.filter((i) => i.status === 'PAGATA').reduce((a, i) => a + (Number(i.total) || 0), 0);
  const cost = p.cost ?? 0;
  const margine = calcMargin(p.revenue, cost);

  const prossima = [...p.milestones].sort((a, b) => a.order - b.order).find((m) => !m.done);
  const giorniMs = prossima?.dueDate ? giorniA(prossima.dueDate.slice(0, 10) + 'T00:00:00Z') : null;
  const modello = p.model ? p.model.replace(/_/g, ' ').toLowerCase() : null;

  return (
    <MPage>
      <MHead
        title={cosaE(p.name)}
        sub={[nomeCliente(p.client), DIVISION_LABEL[p.division], modello, p.pm ? `segue ${p.pm.firstName} ${p.pm.lastName}` : null].filter(Boolean).join(' · ')}
      />

      <MHero label={label} value={value} sub={sotto.map((x, i) => <span key={i}>{i > 0 && ' · '}{x}</span>)} />

      <LavoriMobile projectId={p.id} tasks={p.tasks} communications={p.communications} />

      <MGroup title="Da fare" count={aperti.length || undefined}>
        {aperti.length
          ? aperti.slice(0, 5).map((t) => <RigaTask key={t.id} t={t} />)
          : <MEmpty>{r.totale > 0 ? 'Nessun task oltre ai lavori previsti.' : 'Nessun task aperto.'}</MEmpty>}
        {p.tasks.length > 0 && (
          <MRow icon="tasks" title="Tutti i task del progetto" trail={p.tasks.length} trailTone="mute" onClick={() => onSezione('task')} />
        )}
      </MGroup>

      <MGroup title="Diario" count={p.communications.length || undefined} action={{ label: 'Scrivi', onClick: () => openModal('newComm', { projectId: p.id }) }}>
        {ultime.length
          ? ultime.map((c) => <RigaComunicazione key={c.id} c={c} onApri={() => setComm(c)} />)
          : <MEmpty>Nessuna comunicazione registrata: email, chiamate e incontri con il cliente si scrivono qui.</MEmpty>}
        {(p.communications.length > 3 || p.activity.length > 0) && (
          <MRow icon="list" title="Tutto il diario" sub={p.activity.length ? `anche le ${p.activity.length} attività registrate` : undefined} onClick={() => onSezione('diario')} />
        )}
      </MGroup>

      <MGroup
        title="Soldi"
        note={p.projectRecurring?.length ? 'La rata è lo stesso valore pagato a mesi: non si somma.' : undefined}
      >
        <MRow
          icon="euro"
          title="Valore del progetto"
          sub={p.deal ? `venduto con la trattativa "${p.deal.title}"` : p.revenue > 0 ? undefined : 'nessun documento dice ancora quanto vale'}
          trail={p.revenue > 0 ? euro(p.revenue) : 'da definire'}
          trailTone={p.revenue > 0 ? undefined : 'mute'}
          onClick={() => onSezione('gestione')}
        />
        {fattureValide.length > 0 && (
          <MRow
            icon="invoice"
            title="Fatturato"
            sub={incassato >= fatturato ? 'tutto incassato' : incassato > 0 ? `incassati ${euro(incassato)}` : 'niente ancora incassato'}
            trail={euro(fatturato)}
            onClick={() => onSezione('gestione')}
          />
        )}
        {(p.projectRecurring ?? []).map((s) => (
          <MRow
            key={s.id}
            icon="repeat"
            title={s.name}
            sub={s.endDate ? `rata fino al ${giornoBreve(s.endDate)}` : 'rata senza scadenza'}
            trail={`${euro(s.amountNet)}${s.cycle === 'MONTHLY' ? '/mese' : ''}`}
          />
        ))}
        {/* 26/10/2026: al posto del vecchio "Margine" (ricavo meno costo) il conto vero, come sul computer. */}
        {showMargin && <ContoRigheMobile projectId={p.id} />}
        {!!p.clientInvoices?.length && (
          <MRow icon="receipt" title="Fatture del cliente" sub="anche quelle di altri lavori" trail={p.clientInvoices.length} trailTone="mute" onClick={() => setFoglio('fatture')} />
        )}
        {!!p.clientSubscriptions?.length && (
          <MRow icon="repeat" title="Abbonamenti del cliente" sub="ricavi ricorrenti, fuori dal valore" trail={p.clientSubscriptions.length} trailTone="mute" onClick={() => setFoglio('abbonamenti')} />
        )}
      </MGroup>

      <MGroup title="Il progetto">
        {/* Lo stato è quello della bacheca del computer, dove si cambia
            trascinando: qui si tocca la riga e si sceglie (stessa chiamata,
            senza toccare l'ordine nella colonna). */}
        <MRow
          icon="kanban"
          title="Stato"
          trail={STATO_PROGETTO[p.status] ?? p.status}
          trailTone="mute"
          onClick={scrive ? () => setStato(true) : undefined}
          chevron={scrive}
        />
        <MRow
          icon="calendar"
          tone={giorniMs != null && giorniMs < 0 ? 'alert' : undefined}
          title="Timeline"
          sub={prossima
            ? `${prossima.label}: ${giorniMs == null ? '' : giorniMs < 0 ? `${-giorniMs} giorni di ritardo` : giorniMs === 0 ? 'oggi' : `fra ${giorniMs} giorni`}`
            : p.milestones.length ? 'tutte le milestone sono fatte' : 'nessuna milestone'}
          onClick={() => onSezione('timeline')}
        />
        <MRow icon="file" title="Documenti" sub={p.documents.length ? undefined : 'nessun documento'} trail={p.documents.length || undefined} trailTone="mute" onClick={() => onSezione('documenti')} />
        <MRow
          icon="users"
          title="Team, ore e conti"
          sub={p.team.length ? `${p.team.length === 1 ? '1 persona' : `${p.team.length} persone`} sul progetto` : 'nessuno assegnato'}
          onClick={() => onSezione('gestione')}
        />
        {!!p.deliverables?.length && (
          <MRow icon="palette" title="Consegne di Design" trail={p.deliverables.length} trailTone="mute" onClick={() => setFoglio('consegne')} />
        )}
        {!!p.notes && (
          <MRow icon="help" title="Da dove vengono questi dati" sub={p.notes.split('\n')[0]} onClick={() => setFoglio('nota')} />
        )}
        <MRow icon="building" title={nomeCliente(p.client)} sub="scheda del cliente" onClick={() => navigate(`/clients/${p.client.id}`)} />
        {p.deal && (
          <MRow
            icon="handshake"
            title={p.deal.title}
            sub={`trattativa d'origine, venduta a ${euro(p.deal.amount)}`}
            onClick={() => navigate(`/trattative/${p.deal!.id}`)}
          />
        )}
        {p.stagingUrl && <MRow icon="link" title="Sito di prova" sub={p.stagingUrl.replace(/^https?:\/\//, '')} onClick={() => window.open(p.stagingUrl!, '_blank', 'noopener,noreferrer')} />}
        {p.repositoryUrl && <MRow icon="link" title="Repository" sub={p.repositoryUrl.replace(/^https?:\/\//, '')} onClick={() => window.open(p.repositoryUrl!, '_blank', 'noopener,noreferrer')} />}
      </MGroup>

      <DesktopOnly
        cosa="Creare un preventivo o eliminare il progetto"
        perche="sono una procedura a passi e una verifica che si fanno meglio sullo schermo grande."
      ><></></DesktopOnly>

      <ComunicazioneSheet projectId={p.id} c={comm} onClose={() => setComm(null)} />

      <MScelte
        open={stato}
        onClose={() => setStato(false)}
        titolo="Stato del progetto"
        nota="È la colonna della bacheca del computer."
        opzioni={[
          { v: 'PREVENTIVO', l: 'In preventivo', sub: 'non ancora cominciato' },
          { v: 'ATTIVO', l: 'Attivo' },
          { v: 'IN_CORSO', l: 'In corso' },
          { v: 'REVISIONE', l: 'In revisione', sub: 'consegnato, il cliente lo sta guardando' },
          { v: 'IN_PAUSA', l: 'In pausa' },
          { v: 'CHIUSO', l: 'Chiuso', sub: 'lavoro consegnato: esce dalla bacheca' },
        ]}
        valore={p.status}
        onScegli={(v) => { setStato(false); if (v !== p.status) sposta.mutate(v); }}
      />

      <MobileSheet open={foglio === 'nota'} onClose={() => setFoglio(null)} title="Da dove vengono questi dati">
        <div className="pjm-sheet"><div className="pjm-testo">{p.notes}</div></div>
      </MobileSheet>

      <MobileSheet open={foglio === 'fatture'} onClose={() => setFoglio(null)} title="Fatture del cliente">
        <div className="pjm-sheet">
          <MGroup title={`Le ultime ${Math.min(6, p.clientInvoices?.length ?? 0)}`} note="Ci sono anche le fatture di altri lavori dello stesso cliente.">
            {(p.clientInvoices ?? []).slice(0, 6).map((f) => (
              <MRow
                key={f.id}
                icon="invoice"
                title={f.number ?? 'senza numero'}
                sub={[f.issueDate ? giornoBreve(f.issueDate) : null, f.projectId === p.id ? 'di questo progetto' : null].filter(Boolean).join(' · ') || undefined}
                trail={euro(Number(f.total) || 0)}
                onClick={() => { setFoglio(null); navigate(`/invoices/${f.id}`); }}
              />
            ))}
          </MGroup>
          <button className="btn m-block" onClick={() => { setFoglio(null); navigate(`/clients/${p.client.id}`); }}>Tutte nella scheda del cliente</button>
        </div>
      </MobileSheet>

      <MobileSheet open={foglio === 'abbonamenti'} onClose={() => setFoglio(null)} title="Abbonamenti del cliente">
        <div className="pjm-sheet">
          <MGroup title="Ricavi ricorrenti" note="Non fanno parte del valore del progetto.">
            {(p.clientSubscriptions ?? []).slice(0, 6).map((s) => (
              <MRow
                key={s.id}
                icon="repeat"
                title={s.name}
                sub={s.active ? (s.endDate ? `fino al ${giornoBreve(s.endDate)}` : 'senza scadenza') : 'non attivo'}
                trail={`${euro(s.amountNet)}${s.cycle === 'MONTHLY' ? '/mese' : ''}`}
                trailTone={s.active ? undefined : 'mute'}
              />
            ))}
          </MGroup>
        </div>
      </MobileSheet>

      <MobileSheet open={foglio === 'consegne'} onClose={() => setFoglio(null)} title="Consegne di Design">
        <div className="pjm-sheet">
          <MGroup title="Consegne del progetto" count={p.deliverables?.length}>
            {(p.deliverables ?? []).map((d) => (
              <MRow
                key={d.id}
                icon="palette"
                title={d.title}
                sub={[STATO_CONSEGNA[d.status] ?? d.status, TIPO_CONSEGNA[d.type] ?? d.type, d.revisions ? `${d.revisions} revisioni` : null, personaBreve(d.owner)].filter(Boolean).join(' · ')}
                trail={d.deadline ? giornoBreve(d.deadline) : undefined}
                trailTone="mute"
              />
            ))}
          </MGroup>
        </div>
      </MobileSheet>
    </MPage>
  );
}

const STATO_CONSEGNA: Record<string, string> = {
  BRIEF: 'Brief', DRAFT: 'Bozza', REVIEW: 'In revisione', APPROVED: 'Approvata', DELIVERED: 'Consegnata',
};
const TIPO_CONSEGNA: Record<string, string> = {
  DESIGN_SYSTEM: 'Design system', PROTOTYPE: 'Prototipo', BRAND_ASSET: 'Asset brand', SOCIAL_ASSET: 'Asset social',
  WEB_PAGE: 'Pagina web', PRESENTATION: 'Presentazione', OTHER: 'Altro',
};
